import axios from 'axios';

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Her istege localStorage'daki JWT token'i Authorization header olarak ekler
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ─────────────────────────────────────────────────────────────
// 401 → otomatik token yenileme (refresh) + istegi tekrarlama
//
// Access token 15 dakikada bir doluyor. 401 gelince:
//   1. /auth/refresh ile yeni token cifti alinir (rotation)
//   2. Orijinal istek yeni token ile tekrarlanir
//   3. Ayni anda birden fazla 401 gelirse TEK refresh yapilir,
//      digerleri ayni promise'i bekler (istek kuyrugu)
//   4. Refresh de basarisiz olursa oturum kapatilip /login'e atilir
// ─────────────────────────────────────────────────────────────
let refreshPromise = null;

// AuthContext buraya bir callback kaydeder; boylece oturum kapaninca React
// state'i de temizlenir ve admin kabugu ANINDA unmount olur. Onceden yalnizca
// window.location.href kullaniliyordu ve panel, sayfa yenilenene kadar
// "girisli" gorunmeye devam ediyordu.
let onSessionExpired = null;
export const setSessionExpiredHandler = (fn) => {
    onSessionExpired = fn;
};

// Kuyruktaki her istek ayri ayri forceLogout cagirabiliyordu; tek seferlik.
let loggingOut = false;

export const forceLogout = ({ expired = true } = {}) => {
    if (loggingOut) return;
    loggingOut = true;

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');

    // Giris sayfasi "oturumunuz sona erdi" mesajini bu bayrakla gosterir
    if (expired) {
        try {
            sessionStorage.setItem('sessionExpired', '1');
        } catch {
            // sessionStorage erisilemezse mesaj gosterilmez, akis bozulmaz
        }
    }

    if (onSessionExpired) {
        onSessionExpired();
    }

    // /login'e YALNIZCA panel icindeyken yonlendir. AuthProvider tum
    // uygulamayi sardigi icin bu fonksiyon "/" ve "/embed" sayfalarinda da
    // tetiklenebiliyor; orada ziyaretci musteri, onu admin girisine atmak
    // widget'i login formuna cevirir (embed iframe'inde bilhassa kotu).
    const path = window.location.pathname;
    const inAdmin = path === '/admin' || path.startsWith('/admin/');
    if (inAdmin) {
        window.location.href = '/login';
    }
};

/**
 * Access token'i yeniler. Tek-ucus: ayni anda birden fazla cagri gelirse
 * tek istek atilir, digerleri ayni promise'i bekler.
 * Hem 401 interceptor'i hem AuthContext'in proaktif zamanlayicisi kullanir.
 */
export const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = refreshTokens().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

const refreshTokens = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        throw new Error('Refresh token bulunamadi');
    }
    // apiClient degil ham axios — interceptor dongusune girmesin
    const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
    );
    const { token, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem('token', token);
    if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
    }
    return token;
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;

        // Sadece 401'de devreye gir; login/refresh cagrilarinda ve
        // zaten retry edilmis isteklerde tekrar deneme
        const isAuthCall =
            original?.url?.includes('/auth/login') ||
            original?.url?.includes('/auth/refresh');

        if (status !== 401 || isAuthCall || original._retry) {
            return Promise.reject(error);
        }
        original._retry = true;

        try {
            const newToken = await refreshAccessToken();
            original.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(original);
        } catch (refreshError) {
            forceLogout();
            return Promise.reject(refreshError);
        }
    }
);

export const agentService = {
    login: async (username, password) => {
        const response = await apiClient.post('/auth/login', { username, password });
        return response.data;
    },

    logout: async () => {
        await apiClient.post('/auth/logout');
    },
};

export { apiClient };
export default agentService;
